import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "../lib/api";
import { useSiteSettings } from "../contexts/SiteSettingsContext";
import { Gem, ArrowDownToLine, ArrowUpFromLine, ArrowLeft } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso).getTime();
    const diff = Math.max(0, Math.floor((Date.now() - d) / 1000));
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch { return ""; }
}

function ProofTable({ title, icon: Icon, rows, accent }) {
  return (
    <div className="bg-[#131722] rounded-xl border border-[#2A3241] overflow-hidden" data-testid={`proofs-${title.toLowerCase().includes("deposit") ? "deposits" : "withdrawals"}-table`}>
      <div className={`px-5 py-3 border-b border-[#2A3241] flex items-center gap-2`} style={{ background: `${accent}10` }}>
        <Icon className="w-4 h-4" style={{ color: accent }} />
        <h3 className="text-sm font-bold tracking-widest uppercase" style={{ color: accent }}>{title}</h3>
      </div>
      {rows.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-[#64748B]">No entries yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0B0E14]">
              <tr className="text-[10px] uppercase tracking-widest text-[#64748B]">
                <th className="px-4 py-3 text-left font-semibold">User</th>
                <th className="px-4 py-3 text-left font-semibold">TX / Address</th>
                <th className="px-4 py-3 text-left font-semibold">Method</th>
                <th className="px-4 py-3 text-left font-semibold">Currency</th>
                <th className="px-4 py-3 text-right font-semibold">Amount</th>
                <th className="px-4 py-3 text-right font-semibold">Time</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-[#2A3241]/40 hover:bg-[#1C212E]/40 transition-colors">
                  <td className="px-4 py-3 font-medium text-[#F8FAFC]">{r.username}</td>
                  <td className="px-4 py-3 font-mono-game text-xs text-[#94A3B8]">{r.address || "—"}</td>
                  <td className="px-4 py-3 text-[#94A3B8]">{r.method}</td>
                  <td className="px-4 py-3 font-mono-game text-[#FFD600]">{r.currency}</td>
                  <td className="px-4 py-3 text-right font-mono-game font-bold" style={{ color: accent }}>${(r.amount || 0).toFixed(2)}</td>
                  <td className="px-4 py-3 text-right text-xs text-[#64748B]">{timeAgo(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function PaymentProofsPage() {
  const { siteTitle } = useSiteSettings();
  const [data, setData] = useState({ deposits: [], withdrawals: [], settings: { page_enabled: false } });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await API.get("/payment-proofs");
        if (alive) setData(data);
      } catch { /* ignore */ }
      finally { if (alive) setLoading(false); }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (!loading && !data.settings?.page_enabled) {
    return (
      <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center px-4" data-testid="proofs-disabled">
        <div className="text-center max-w-md">
          <p className="text-sm text-[#94A3B8]">Payment proofs are currently unavailable.</p>
          <Link to="/" className="text-[#00F0FF] text-sm hover:underline mt-3 inline-block">← Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0E14] text-[#F8FAFC]" data-testid="payment-proofs-page">
      <header className="border-b border-[#2A3241]/50 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="proofs-home-link">
            <div className="w-10 h-10 rounded-lg bg-[#00F0FF]/10 flex items-center justify-center">
              <Gem className="w-5 h-5 text-[#00F0FF]" />
            </div>
            <span className="text-lg font-bold tracking-tight">{siteTitle}</span>
          </Link>
          <Link to="/" className="text-xs text-[#94A3B8] hover:text-[#F8FAFC] flex items-center gap-1">
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="text-center mb-10">
          <p className="text-xs font-mono-game text-[#FFD600] tracking-[0.3em] uppercase mb-3 glow-gold">Trust &amp; Transparency</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-3">Payment Proofs</h1>
          <p className="text-sm text-[#94A3B8] max-w-xl mx-auto">Latest verified deposits and payouts on the platform. Updated in real-time.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ProofTable title="Latest 10 Deposits" icon={ArrowDownToLine} rows={data.deposits || []} accent="#10B981" />
          <ProofTable title="Latest 10 Withdrawals" icon={ArrowUpFromLine} rows={data.withdrawals || []} accent="#FFD600" />
        </div>
      </main>
    </div>
  );
}
